import { getPlayerById } from '../state/selectors';
import { getInstanceDefinition } from '../cards/cardLookup';
import {
  destroyFromHand, discardFromHand, grantXp, queueHandChoice, applyDoWhatYouCan, battleTargetableHeroes,
  estimateHeroAttackValue, destroyFromDiscard, markInstanceIgnored, removeMonsterFromHallToDiscard,
  isHeroLike, findEligibleShieldBearer, queueShieldBearerOffer, INTELLIGENT_REACT_BONUS, heroDestroyRedirectsToDiscard,
} from '../cards/effectHelpers';
import { endTurn } from './turnActions';
import { compactAndRefill } from '../dungeon/hall';
import { applyBonusPrepare } from './prepareActions';

function updatePlayer(state, playerId, updateFn) {
  return { ...state, players: state.players.map((p) => (p.id === playerId ? updateFn(p) : p)) };
}

// Purple Worm (Caverns of Bane): "Battle: Discard 2 cards or destroy 1
// hero." The first card needing effectHelpers.js's queueEffectChoice - see
// its own comment for why this is data-driven rather than embedding
// resolver functions directly in state. Keyed by cardId so a future card
// needing this same shape just adds its own entry here.
const EFFECT_CHOICE_RESOLVERS = {
  'monster-purple-worm': {
    discard: (state, playerId) => {
      const player = getPlayerById(state, playerId);
      const toDiscardCount = applyDoWhatYouCan(2, player.hand).length;
      let nextState = state;
      for (let i = 0; i < toDiscardCount; i += 1) {
        const current = getPlayerById(nextState, playerId);
        nextState = queueHandChoice(nextState, {
          playerId, verb: 'discard', candidateInstanceIds: [...current.hand], reason: 'Purple Worm: discard 2 cards.',
        });
      }
      return nextState;
    },
    destroy: (state, playerId) => {
      const player = getPlayerById(state, playerId);
      const candidateInstanceIds = battleTargetableHeroes(state, player);
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Purple Worm: destroy 1 hero.' });
    },
  },
};

// Caverns of Bane, Rot Grub's Raid (see effectHelpers.js's
// queueAttackValueChoice): "defeat this card (you gain the XP)." A
// simplified subset of combat/resolveCombat.js's own win-handling (remove
// from hall, discard, grant XP, refill the hall) - deliberately skips
// chain-kill/Thunderstone-Bearer/spoils-window handling, none of which Rot
// Grub itself needs, rather than reusing the full combat pipeline for a
// trigger that isn't a normal fight at all (it happens well after
// CHOOSE_MONSTER has already finished, once this pending choice is finally
// resolved). No-ops if the monster already left the hall some other way by
// the time this resolves - nothing left to defeat.
function defeatMonsterOutright(state, { playerId, monsterInstanceId }) {
  if (!state.dungeonHall.includes(monsterInstanceId)) return state;
  const monsterDef = getInstanceDefinition(state, monsterInstanceId);
  const nextState = grantXp(removeMonsterFromHallToDiscard(state, playerId, monsterInstanceId), playerId, monsterDef.xp || 0);
  return compactAndRefill(nextState, { playerId });
}

// Two entries for the SAME player can be queued off the same pre-effect hand
// snapshot (e.g. Ettin queues a Battle destroy AND an Aftermath destroy in
// one combat, both computed before either has actually happened) - if
// resolving the first one happens to remove the only card that would have
// been valid for the second, the second entry is left with nothing to
// destroy/discard. Rather than force a player to "resolve" an empty picker
// (there's no valid instanceId that could ever satisfy it, and it's
// mandatory with no Cancel button), drop it silently - "do what you can"
// (effectHelpers.js's applyDoWhatYouCan) applies the same way here. An
// 'optionalDiscard' entry (see resolveOptionalDiscard below) has no
// candidateInstanceIds at all - declining is always a valid choice
// regardless of hand state, so it's never dead-on-arrival. An 'effectChoice'
// entry (see resolveEffectChoice below) is the same way - each of its
// EFFECT_CHOICE_RESOLVERS options degrades to a harmless no-op ("do what you
// can" of zero) rather than ever being flat-out invalid, so there's no
// candidate-presence check that could legitimately drop it either. A
// 'discardPileChoice' entry (Gnoll - see queueDiscardPileChoice) DOES still
// need a presence check like the default case below, just against
// player.discard instead of player.hand (its candidates were revealed FROM
// discard, never touched hand at all). A 'shieldBearerOffer' entry (Shield
// Bearer's React) is inserted at the FRONT of the queue the instant its
// hero target is chosen, and dispatch()'s top-level guard blocks every
// other action while any entry is pending - nothing can run between
// queueing it and it being answered, so both its heroInstanceId and
// shieldBearerInstanceId are always still exactly where they were left;
// like optionalDiscard/effectChoice, it can never go dead-on-arrival. An
// 'intelligentReactOffer' entry (Intelligent's own React) is the same way -
// declining is always valid, and resolveIntelligentReactOffer itself
// tolerates the offered instance having somehow already left hand by then.
// A 'preparePick' entry (Enhanced Rugged Jack's React) has no candidates at
// all - "keep nothing" (orderedInstanceIds: []) is always a legal answer,
// same reasoning as optionalDiscard.
export function dropDeadEntries(state) {
  let queue = state.meta.pendingHandChoices || [];
  while (queue.length) {
    const head = queue[0];
    if (head.kind === 'optionalDiscard' || head.kind === 'effectChoice' || head.kind === 'shieldBearerOffer'
      || head.kind === 'intelligentReactOffer' || head.kind === 'preparePick') break;
    const player = getPlayerById(state, head.playerId);
    const zone = head.kind === 'discardPileChoice' ? player.discard : player.hand;
    if (head.candidateInstanceIds.some((id) => zone.includes(id))) break;
    queue = queue.slice(1);
  }
  const nextState = { ...state, meta: { ...state.meta, pendingHandChoices: queue } };
  // e.g. Thundermage Bolter's "End your turn" got deferred by
  // useHandCardAbility because resolving its hall refill queued a mandatory
  // Raid destroy-choice first (see abilityActions.js) - now that the whole
  // queue has actually drained, honor the deferred end-turn.
  if (!queue.length && nextState.meta.endTurnOverride) {
    return endTurn({ ...nextState, meta: { ...nextState.meta, endTurnOverride: false } });
  }
  return nextState;
}

// Resolves the oldest pending entry in meta.pendingHandChoices (see
// effectHelpers.js's queueHandChoice) - a FIFO queue rather than a single
// slot, since a single trigger can require more than one choice at once
// (e.g. a hall refill revealing 2 Raid cards, or "discard 2 cards" queuing
// one entry per card, or "each player destroys 1 card" queuing one entry
// per player). dispatch()'s top-level guard keeps this the only action
// available while the queue is non-empty. Deliberately does NOT assert the
// resolving player is the active player - unlike every other action, some
// entries (Charred Bruin, Hellhound) are owed to a non-active player, who
// must still be able to resolve their own entry. pending.playerId is the
// only identity check that matters here.
export function resolveHandChoice(state, { playerId, instanceId }) {
  const queue = state.meta.pendingHandChoices || [];
  const pending = queue[0];
  if (!pending || pending.playerId !== playerId) {
    throw new Error('No hand choice is currently owed to you.');
  }
  if (pending.kind === 'optionalDiscard') {
    throw new Error('This is an opt-in/decline choice, not a card pick - use RESOLVE_OPTIONAL_DISCARD instead.');
  }
  if (pending.kind === 'effectChoice') {
    throw new Error('This is a choice between effects, not a card pick - use RESOLVE_EFFECT_CHOICE instead.');
  }
  if (pending.kind === 'shieldBearerOffer') {
    throw new Error('This is a Shield Bearer opt-in/decline choice, not a card pick - use RESOLVE_SHIELD_BEARER_OFFER instead.');
  }
  if (pending.kind === 'intelligentReactOffer') {
    throw new Error('This is an Intelligent React opt-in/decline choice, not a card pick - use RESOLVE_INTELLIGENT_REACT_OFFER instead.');
  }
  if (pending.kind === 'optionalCurseSelfDestroy') {
    throw new Error('This is an opt-in/decline choice, not a card pick - use RESOLVE_OPTIONAL_CURSE_DESTROY instead.');
  }
  if (pending.kind === 'preparePick') {
    throw new Error('This is a prepare-action card pick, not a single-card choice - use RESOLVE_PREPARE_PICK instead.');
  }
  if (!pending.candidateInstanceIds.includes(instanceId)) {
    throw new Error('That card is not a valid choice for this effect.');
  }
  const player = getPlayerById(state, playerId);

  // Gnoll's own shape (effectHelpers.js's queueDiscardPileChoice): the
  // candidates were revealed FROM the discard pile, not hand - every other
  // kind below assumes hand membership.
  if (pending.kind === 'discardPileChoice') {
    if (!player.discard.includes(instanceId)) throw new Error('That card is not in your discard pile.');
    const resolved = destroyFromDiscard(state, playerId, instanceId);
    return dropDeadEntries({ ...resolved, meta: { ...resolved.meta, pendingHandChoices: queue.slice(1) } });
  }

  if (!player.hand.includes(instanceId)) throw new Error('That card is not in your hand.');

  // Rot Grub's own shape (effectHelpers.js's queueAttackValueChoice): still
  // "pick 1 card," but the OUTCOME branches on the chosen card's own
  // computed Attack Value against a threshold, rather than a fixed verb.
  if (pending.kind === 'attackValueChoice') {
    const attackValue = estimateHeroAttackValue(state, player, instanceId);
    const restQueue = queue.slice(1);
    if (attackValue >= pending.threshold) {
      const baseState = { ...state, meta: { ...state.meta, pendingHandChoices: restQueue } };
      return dropDeadEntries(defeatMonsterOutright(baseState, { playerId, monsterInstanceId: pending.monsterInstanceId }));
    }
    return dropDeadEntries(destroyHeroOrOfferShieldBearer(state, playerId, instanceId, restQueue));
  }

  // War Incarnate's own verb (effectHelpers.js's markInstanceIgnored) -
  // 'ignore' leaves the card in hand, unlike discard/destroy.
  if (pending.verb === 'discard') {
    const resolved = discardFromHand(state, playerId, instanceId);
    return dropDeadEntries({ ...resolved, meta: { ...resolved.meta, pendingHandChoices: queue.slice(1) } });
  }
  if (pending.verb === 'ignore') {
    const resolved = markInstanceIgnored(state, playerId, instanceId);
    return dropDeadEntries({ ...resolved, meta: { ...resolved.meta, pendingHandChoices: queue.slice(1) } });
  }
  return dropDeadEntries(destroyHeroOrOfferShieldBearer(state, playerId, instanceId, queue.slice(1)));
}

// Numenera, Shield Bearer's React (effectHelpers.js's
// queueShieldBearerOffer/findEligibleShieldBearer): intercepts here rather
// than inside destroyFromHand itself - every monster/bearer Battle/
// Aftermath/Raid "destroy 1 hero" effect already funnels through this exact
// point once a specific card has actually been chosen (either directly
// above, or via attackValueChoice's "otherwise destroy that hero" branch),
// so it's the one place that can offer the substitution without touching
// each individual handler. Only the 'destroy' verb is covered - Shield
// Bearer's own printed text says "destroyed," not "discarded" (unlike Pike/
// Rage of the Disowned, which cover both). A handful of direct, non-queued
// destroyFromHand calls elsewhere (Smokeplume Drake's algorithmic "destroy
// highest-Strength hero," Scabbark's "destroy your whole hand") are NOT
// covered by this pass - see docs/expansion-audit.md.
// Worlds Collide, Guardian Blade: "specialEffects: If the equipped hero
// would be destroyed, discard that hero instead." / Ambrosia: "Whenever a
// Battle or Aftermath effect destroys a hero, discard that hero instead."
// (a Dungeon-ability GRANT, turnFlags.battleAftermathDestroyRedirectsToDiscard,
// not always-active like Guardian Blade). Both are unconditional redirects
// (no choice to offer), checked BEFORE the Shield Bearer/Border Guard offer
// just below - if a hero was never actually going to be destroyed, there's
// nothing left to protect it FROM.
function destroyHeroOrOfferShieldBearer(state, playerId, instanceId, restQueue) {
  const player = getPlayerById(state, playerId);
  const def = getInstanceDefinition(state, instanceId);
  if (heroDestroyRedirectsToDiscard(state, playerId, instanceId, def)) {
    const resolved = discardFromHand(state, playerId, instanceId);
    return { ...resolved, meta: { ...resolved.meta, pendingHandChoices: restQueue } };
  }
  if (isHeroLike(def)) {
    const shieldBearerId = findEligibleShieldBearer(state, player);
    if (shieldBearerId && shieldBearerId !== instanceId) {
      const baseState = { ...state, meta: { ...state.meta, pendingHandChoices: restQueue } };
      const shieldBearerTitle = getInstanceDefinition(state, shieldBearerId).title;
      return queueShieldBearerOffer(baseState, {
        playerId,
        heroInstanceId: instanceId,
        shieldBearerInstanceId: shieldBearerId,
        reason: `${shieldBearerTitle}: destroy this card instead of the hero that would be destroyed?`,
      });
    }
  }
  const resolved = destroyFromHand(state, playerId, instanceId);
  return { ...resolved, meta: { ...resolved.meta, pendingHandChoices: restQueue } };
}

// Resolves the shieldBearerOffer queued by destroyHeroOrOfferShieldBearer
// above. Accepting destroys the Shield Bearer instead (and marks its React
// used this turn, same usedAbilityInstanceIds bookkeeping as any other
// ability); declining - or the Shield Bearer having somehow already left
// hand by the time this resolves - falls through to destroying the
// original hero, same "do what you can" tolerance as every other queued
// entry (dropDeadEntries handles the hero itself no longer being present).
export function resolveShieldBearerOffer(state, { playerId, accept }) {
  const queue = state.meta.pendingHandChoices || [];
  const pending = queue[0];
  if (!pending || pending.playerId !== playerId || pending.kind !== 'shieldBearerOffer') {
    throw new Error('No Shield Bearer offer is currently owed to you.');
  }
  const restQueue = queue.slice(1);
  const baseState = { ...state, meta: { ...state.meta, pendingHandChoices: restQueue } };
  const player = getPlayerById(state, playerId);
  if (accept && player.hand.includes(pending.shieldBearerInstanceId)) {
    const used = updatePlayer(baseState, playerId, (p) => ({
      ...p,
      turnFlags: { ...p.turnFlags, usedAbilityInstanceIds: [...p.turnFlags.usedAbilityInstanceIds, `${pending.shieldBearerInstanceId}:react:0`] },
    }));
    return dropDeadEntries(destroyFromHand(used, playerId, pending.shieldBearerInstanceId));
  }
  if (!player.hand.includes(pending.heroInstanceId)) return dropDeadEntries(baseState);
  return dropDeadEntries(destroyFromHand(baseState, playerId, pending.heroInstanceId));
}

// Resolves the intelligentReactOffer queued by effectHelpers.js's
// offerIntelligentReact. Accepting grants that specific card's own bonus
// (Physical Attack, +Light for the L3 line) and marks its React used this
// turn - a DIFFERENT Intelligent instance in the same hand is untouched
// (per-instance cap, not "once per turn for the whole ability" - rulebook:
// "If you have two or more identical cards with React abilities... you may
// use multiples during a single turn"). Declining, or the offered instance
// having somehow already left hand by the time this resolves, is a no-op.
export function resolveIntelligentReactOffer(state, { playerId, accept }) {
  const queue = state.meta.pendingHandChoices || [];
  const pending = queue[0];
  if (!pending || pending.playerId !== playerId || pending.kind !== 'intelligentReactOffer') {
    throw new Error('No Intelligent React offer is currently owed to you.');
  }
  const restQueue = queue.slice(1);
  const baseState = { ...state, meta: { ...state.meta, pendingHandChoices: restQueue } };
  const player = getPlayerById(state, playerId);
  if (!accept || !player.hand.includes(pending.instanceId)) return dropDeadEntries(baseState);
  const bonus = INTELLIGENT_REACT_BONUS[getInstanceDefinition(state, pending.instanceId).id];
  const used = updatePlayer(baseState, playerId, (p) => ({
    ...p,
    turnFlags: {
      ...p.turnFlags,
      usedAbilityInstanceIds: [...p.turnFlags.usedAbilityInstanceIds, `${pending.instanceId}:react:0`],
      bonusPhysicalAttack: p.turnFlags.bonusPhysicalAttack + bonus.physicalAttack,
      bonusLight: p.turnFlags.bonusLight + bonus.light,
    },
  }));
  return dropDeadEntries(used);
}

// Resolves the preparePick queued by effectHelpers.js's queuePreparePick
// (Enhanced Rugged Jack's React). orderedInstanceIds is validated against
// the CURRENT hand by actions/prepareActions.js's applyBonusPrepare itself
// (same "recheck at resolution time" as every other queued entry) - an
// empty array ("keep nothing") is always legal.
export function resolvePreparePick(state, { playerId, orderedInstanceIds }) {
  const queue = state.meta.pendingHandChoices || [];
  const pending = queue[0];
  if (!pending || pending.playerId !== playerId || pending.kind !== 'preparePick') {
    throw new Error('No prepare pick is currently owed to you.');
  }
  const restQueue = queue.slice(1);
  const baseState = { ...state, meta: { ...state.meta, pendingHandChoices: restQueue } };
  const applied = applyBonusPrepare(baseState, playerId, orderedInstanceIds || []);
  return dropDeadEntries(applied);
}

// Blazing Grimalkin's shape is different from every other queued hand
// choice: "each player may choose to discard 3 cards. If a player chooses
// not to, that player loses 1 XP" isn't "pick 1 of several candidates" -
// it's an opt-in/decline gate, and only THEN (if accepted) a card pick.
// Declining applies the XP penalty immediately and moves on. Accepting
// queues `count` ordinary single-discard entries (effectHelpers.js's
// queueHandChoice, verb 'discard') computed from the CURRENT hand at
// accept-time - not the hand snapshot from whenever Battle originally fired
// - reusing the exact same "discard N cards, one at a time" mechanism
// Smokeplume Drake's "discard 2 cards" already established, inserted right
// after this entry so any other still-pending player's own entry (this card
// affects "each player") is asked next before circling back.
// Caverns of Bane, Curse of War: "Select 2 random cards from your hand
// (excluding this card). You may destroy 1 of them to destroy this card."
// candidateInstanceIds are the 2 already-randomly-chosen cards (see
// effectHelpers.js's queueOptionalCurseSelfDestroy); declining is a pure
// no-op (the 2 revealed cards simply stay in hand, curse remains).
// Accepting requires the chosen instanceId be one of those exact 2 -
// destroyFromHand is curse-aware either way (a curse-trait candidate
// returns to the bottom of the curse deck rather than vanishing, same as
// every other "destroy a card" effect since the disease/curse fix), and
// destroying the curse itself the same way covers the same case for War
// itself.
export function resolveOptionalCurseSelfDestroy(state, { playerId, accept, instanceId }) {
  const queue = state.meta.pendingHandChoices || [];
  const pending = queue[0];
  if (!pending || pending.playerId !== playerId || pending.kind !== 'optionalCurseSelfDestroy') {
    throw new Error('No Curse of War choice is currently owed to you.');
  }
  const restQueue = queue.slice(1);
  if (!accept) {
    return dropDeadEntries({ ...state, meta: { ...state.meta, pendingHandChoices: restQueue } });
  }
  if (!pending.candidateInstanceIds.includes(instanceId)) {
    throw new Error('That card is not one of the 2 randomly-selected candidates.');
  }
  const afterCandidate = destroyFromHand(state, playerId, instanceId);
  const afterSelf = destroyFromHand(afterCandidate, playerId, pending.selfInstanceId);
  return dropDeadEntries({ ...afterSelf, meta: { ...afterSelf.meta, pendingHandChoices: restQueue } });
}

export function resolveOptionalDiscard(state, { playerId, accept }) {
  const queue = state.meta.pendingHandChoices || [];
  const pending = queue[0];
  if (!pending || pending.playerId !== playerId || pending.kind !== 'optionalDiscard') {
    throw new Error('No optional-discard choice is currently owed to you.');
  }
  const restQueue = queue.slice(1);
  if (!accept) {
    const declined = grantXp(state, playerId, -(pending.declineXpPenalty || 0));
    return dropDeadEntries({ ...declined, meta: { ...declined.meta, pendingHandChoices: restQueue } });
  }
  const player = getPlayerById(state, playerId);
  const toDiscardCount = applyDoWhatYouCan(pending.count, player.hand).length;
  let nextState = { ...state, meta: { ...state.meta, pendingHandChoices: restQueue } };
  for (let i = 0; i < toDiscardCount; i += 1) {
    nextState = queueHandChoice(nextState, {
      playerId, verb: 'discard', candidateInstanceIds: [...player.hand], reason: pending.reason,
    });
  }
  return nextState;
}

// See effectHelpers.js's queueEffectChoice for why this is data-driven
// (pending.cardId + the chosen key look up a resolver in
// EFFECT_CHOICE_RESOLVERS above) rather than storing a function in state.
// The chosen resolver may itself queue further ordinary queueHandChoice
// entries (e.g. Purple Worm's "destroy 1 hero" needs a card target) -
// dropDeadEntries at the end catches that, same as every other resolve
// path here.
export function resolveEffectChoice(state, { playerId, key }) {
  const queue = state.meta.pendingHandChoices || [];
  const pending = queue[0];
  if (!pending || pending.playerId !== playerId || pending.kind !== 'effectChoice') {
    throw new Error('No effect choice is currently owed to you.');
  }
  if (!pending.options.some((o) => o.key === key)) {
    throw new Error('That is not a valid option for this effect.');
  }
  const resolver = EFFECT_CHOICE_RESOLVERS[pending.cardId]?.[key];
  if (!resolver) throw new Error(`No resolver registered for ${pending.cardId}'s "${key}" option.`);
  const restQueue = queue.slice(1);
  const resolved = resolver({ ...state, meta: { ...state.meta, pendingHandChoices: restQueue } }, playerId);
  return dropDeadEntries(resolved);
}
